import { Facebook, Linkedin, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { track } from "@/lib/analytics";

interface SocialShareWidgetProps {
  url?: string;
  title?: string;
  description?: string;
  className?: string;
  size?: "sm" | "md" | "lg";
  variant?: "follow" | "share";
}

export function SocialShareWidget({ 
  url = window.location.href, 
  title = "SOL-VI - Duurzame Energieoplossingen",
  description = "Specialist in zonnepanelen, warmtepompen, thuisbatterijen en laadpalen in Elst en regio Gelderland.",
  className = "",
  size = "md",
  variant = "share"
}: SocialShareWidgetProps) {
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);
  const encodedDescription = encodeURIComponent(description);

  const facebookUrl = variant === "follow" 
    ? "https://www.facebook.com/profile.php?id=61565954054747"
    : `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
    
  const linkedinUrl = variant === "follow"
    ? "https://www.linkedin.com/company/sol-vi/?viewAsMember=true"
    : `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}&title=${encodedTitle}&summary=${encodedDescription}`;

  const handleShare = (platform: string, shareUrl: string) => {
    track('outbound_click', { 
      url: shareUrl, 
      link_text: variant === "follow" ? `Follow ${platform}` : `Share ${platform}`,
      action: variant 
    });
    
    if (variant === "share") {
      window.open(shareUrl, '_blank', 'width=600,height=400');
    } else {
      window.open(shareUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title,
          text: description,
          url
        });
        track('share', { method: 'native' });
      } catch (err) {
        // User cancelled
      }
    }
  };

  const buttonSize = size === "sm" ? "sm" : size === "lg" ? "lg" : "default";
  const iconSize = size === "sm" ? "h-4 w-4" : size === "lg" ? "h-6 w-6" : "h-5 w-5";

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {variant === "follow" && (
        <span className="text-sm font-medium text-muted-foreground mr-2">
          Volg ons:
        </span>
      )}
      
      <Button
        variant="outline"
        size={buttonSize}
        onClick={() => handleShare('Facebook', facebookUrl)}
        className="flex items-center gap-2"
      >
        <Facebook className={iconSize} />
        {variant === "follow" ? "Facebook" : "Deel"}
      </Button>
      
      <Button
        variant="outline"
        size={buttonSize}
        onClick={() => handleShare('LinkedIn', linkedinUrl)}
        className="flex items-center gap-2"
      >
        <Linkedin className={iconSize} />
        {variant === "follow" ? "LinkedIn" : "Deel"}
      </Button>

      {variant === "share" && navigator.share && (
        <Button
          variant="outline"
          size={buttonSize}
          onClick={handleNativeShare}
          className="flex items-center gap-2"
        >
          <Share2 className={iconSize} />
          Delen
        </Button>
      )}
    </div>
  );
}